import type { QueryView } from '@krp/client-runtime'

import type { ActionView } from '../../../shared/views'
import type { CropBox, PhotoCropReviewVm } from '../../common-photos/model/types'

/** Где фотография лежит у источника (`PhotoOccurrenceDto`). */
export interface PhotoOccurrence {
  resourceId: string
  remoteProductKey: string
  fileKey: string
  sha256: string
  matchMethod: string
  isMainAtSource: boolean
  seenAt: string
}

/** Фотография реестра (`PhotoIdentityDto`): ключ, метка, адрес превью на Platform и места, где она лежит. */
export interface PhotoIdentity {
  photoKey: string
  token: string
  previewUrl: string
  occurrences: readonly PhotoOccurrence[]
}

/** Пара «возможно, то же» (`PhotoPairDto`). */
export interface PhotoPair {
  keyA: string
  keyB: string
  hashDistance: number | null
  cosine: number | null
}

/** Как прочитан источник (`PhotoSourceStateDto`): `unknown` — не прочитан, ответ «нет» давать нельзя. */
export interface PhotoSourceState {
  resourceId: string
  unknown: boolean
  attemptedAt: string
  readAt: string | null
  errorCode: string | null
  errorMessage: string | null
}

export interface PhotoRegistry {
  productId: string
  photos: readonly PhotoIdentity[]
  similar: readonly PhotoPair[]
  sources: readonly PhotoSourceState[]
}

/** Временная загрузка (`PhotoStagingDto`): лежит у нас до первой отправки. */
export interface PhotoStaging {
  stagingId: string
  productId: string
  photoKey: string
  token: string
  /** До какого времени загрузка хранится; `null` — без срока (фото поднято из прежнего хранилища общего каталога, PHI-10). */
  expiresAt: string | null
  /** Адрес просмотра загрузки на Platform. */
  contentUrl: string
}

/** Ответ на отправку (`PhotoSendResultDto`): задача в службе синхронизации либо `unchanged` — у источника уже лежит. */
export interface PhotoSendResult {
  jobId: string | null
  state: string | null
  outcome: string
}

export const JOB_STATES = ['queued', 'running', 'completed', 'failed'] as const
export type SendJobState = (typeof JOB_STATES)[number]

export interface SendJobItem {
  state: string
  errorCode: string | null
  message: string | null
  requestId: string | null
  targetResourceId: string
}

/** Задача отправки в службе синхронизации: общее состояние и строки (отказ источника — в `message` дословно). */
export interface SendJob {
  id: string
  state: SendJobState
  errorMessage: string | null
  items: readonly SendJobItem[]
}

/** Пределы источника из профиля вида (`PhotoSourceLimitsDto`): `maxPhotos = null` — без предела; `note` — правило словами от самого вида. */
export interface PhotoSourceLimits {
  maxPhotos: number | null
  maxBytes: number
  note: string
}

/**
 * Источник фото товара из ответа сервера (`GET …/photo-sources`, `PhotoSourceDto`): подписи, что умеет вид и пределы. Окно рисует слайдер, отметки и
 * кнопки только по нему. `status = unknown` — пределы не прочитаны, `error` дословно, кнопки источника выключены.
 */
export interface PhotoSource {
  resourceId: string
  kind: string
  title: string
  shortTitle: string
  displayName: string
  rank: number
  supports: { read: boolean; add: boolean; delete: boolean }
  limits: PhotoSourceLimits | null
  status: 'ok' | 'unknown'
  error: string | null
}

export type SlotState = 'present' | 'unknown' | 'absent'

/** Идущая или завершённая отправка в источник — словами и с дословным отказом. */
export interface SendProgressVm {
  phase: 'sending' | 'running' | 'done' | 'failed' | 'unchanged'
  text: string
  error: { code: string; message: string; requestId: string | null } | null
}

/** Строка источника под фотографией. */
export interface SlotVm {
  key: string
  resourceId: string
  /** Подпись источника: короткая подпись вида, а при двух источниках одного вида — ещё и имя экземпляра. */
  title: string
  state: SlotState
  /** Источник умеет принимать фото (`supports.add`): без этого кнопки «Отправить в …» нет. */
  canAdd: boolean
  /** Правило источника словами (`limits.note`), когда он держит одну картинку; иначе `null`. */
  limitNote: string | null
  /** Источник держит одну картинку, и она уже стоит: отправка заменит её — нужно подтверждение с текстом `limitNote`. */
  replaces: boolean
  progress: SendProgressVm | null
  canSend: boolean
  blockedReason: string | null
  /** Фото лежит у источника, и вид умеет удалять (`supports.delete`): только тогда есть кнопка «Удалить из …». У AQSI её нет. */
  canDelete: boolean
  /** Ход удаления из источника словами и с дословным отказом; `null` — не удаляли. */
  deleteProgress: SendProgressVm | null
  /** Почему кнопка «Удалить из …» выключена (идёт отправка или удаление), иначе `null`. */
  deleteBlockedReason: string | null
}

export interface PhotoTileVm {
  /** Ключ фотографии реестра либо `staging:<id>` у временной загрузки. */
  id: string
  photoKey: string | null
  stagingId: string | null
  thumbUrl: string | null
  /** Главное: так помечено у первого по порядку источника, где главное есть. У временной загрузки главного нет. */
  isMain: boolean
  /** Фото пока нигде не лежит — это временная загрузка. */
  pending: boolean
  slots: readonly SlotVm[]
  /** Ход «Убрать загруженное» у временной загрузки; `null` — не убирали. У фотографии реестра всегда `null`. */
  removal: SendProgressVm | null
}

export interface PairVm {
  id: string
  keyA: string
  keyB: string
  thumbA: string | null
  thumbB: string | null
  /** Почему считаем похожими, словами. */
  because: string
}

export interface SourceProblemVm {
  resourceId: string
  title: string
  code: string | null
  message: string
}

export interface PhotoRegistryVm {
  query: QueryView
  tiles: readonly PhotoTileVm[]
  pairs: readonly PairVm[]
  /** Источники, которые не удалось прочитать: текст дословно; для каждого — «неизвестно», а не «нет». */
  problems: readonly SourceProblemVm[]
  /** Итог последнего решения «Это одно / Разные». */
  decision: ActionView
  /** Идущее «Добавить фото»; `null` — не идёт. */
  adding: AddPhotoVm | null
  /** Источники фото не прочитаны: причина дословно. */
  sourcesError: ActionView['error']
  /** Есть ли хоть один источник, в который можно отправлять. */
  hasSources: boolean
}

/**
 * «Добавить фото»: файл → подготовка (вырез и предложенная рамка) → правка рамки → предпросмотр результата → «Сохранить». Пока человек не нажал «Сохранить»,
 * на сервере ничего не лежит. `review` — вырез для правки рамки, `previewUrl` — кадр, каким он получится.
 */
export type AddPhotoPhase = 'preparing' | 'review' | 'previewing' | 'preview' | 'saving' | 'failed'

export interface AddPhotoVm {
  phase: AddPhotoPhase
  fileName: string
  /** Слова этапа («Готовлю фото», «Строю предпросмотр», «Сохраняю»); для остальных этапов пусто. */
  stage: string
  review: PhotoCropReviewVm | null
  /** Рамка человека на этапах после правки; `null` — правки ещё не было. */
  box: CropBox | null
  previewUrl: string | null
  /** Отказ сервера дословно; не пропадает, пока человек не закроет или не повторит. */
  error: ActionView['error']
}

export interface PhotoRegistryActions {
  reload: () => void
  /** Человек выбрал файл: подготовка выреза и рамки. */
  addFile: (file: File) => void
  /** «Показать результат»: рамка принята, строится предпросмотр. */
  previewBox: (box: CropBox) => void
  /** Вернуться от предпросмотра к правке рамки. */
  editBox: () => void
  /** «Сохранить» — положить кадр во временную загрузку (никуда не отправляется). */
  saveAdded: () => void
  /** «Отменить» / «Закрыть»: ничего не сохранено. */
  cancelAdd: () => void
  /** `replace` — человек подтвердил замену стоящей у источника картинки. */
  send: (tile: PhotoTileVm, slot: SlotVm, replace: boolean) => void
  dismissSend: (slot: SlotVm) => void
  /** «Убрать загруженное»: человек подтвердил, временная загрузка удаляется у нас (в источниках её нет). Повтор — тем же вызовом. */
  removeStaged: (tile: PhotoTileVm) => void
  /** «Удалить из …»: человек подтвердил. Повтор после отказа или обрыва — тем же вызовом и с тем же ключом операции. */
  deleteFromSource: (tile: PhotoTileVm, slot: SlotVm) => void
  /** Убрать сообщение о ходе или отказе удаления: у источника (`slot`) либо у временной загрузки (`slot = null`). */
  dismissDelete: (tile: PhotoTileVm, slot: SlotVm | null) => void
  decide: (pair: PairVm, decision: 'same' | 'different') => void
}
